// Match PrincipleGlyph: one simple symbol on a 24-unit grid, not a miniature board diagram.
const question = "M7 7a5 5 0 0 1 10 0c0 4-5 4-5 8m0 5v1";

export const PATTERN_GLYPHS = {
  capture: {
    label: "Capture · structural observation",
    definition:
      "A piece was taken in the displayed position. This records the board action, unlike a missed-capture icon, which identifies an alternative not played.",
    path: "m5 5 14 14M19 5 5 19",
  },
  castle: {
    label: "Castling · structural observation",
    definition:
      "The king and rook moved together in a legal castling move. The symbol identifies the move, not a claim that castling was the best choice.",
    path: "M5 20h14M7 20V10L5 7V3h4v4h6V3h4v4l-2 3v10",
  },
  development: {
    label: "Development · structural observation",
    definition:
      "A piece moved into play from its starting area. This identifies a positional action; principle evidence separately explains whether development helped.",
    path: "M12 20V4m-6 6 6-6 6 6M5 20h14",
  },
  engine_opportunity: {
    label: "Tactical opportunity",
    definition:
      "A verified tactical improvement without a more specific motif. Inspect the supporting continuation to see what was available.",
    path: "m13 3-9 11h7l-1 7L20 9h-7Z",
  },
  missed_check: {
    label: "Missed check",
    definition:
      "A checking alternative identified by analysis. Check is not automatically best; the board evidence explains the missed opportunity.",
    path: "M12 3a9 9 0 1 0 9 9M8 12h8m-4-4v8",
  },
  missed_capture: {
    label: "Missed capture",
    definition:
      "A capture identified as an improvement over the played move. Inspect the verified line; not every available capture is good.",
    path: "M12 3a9 9 0 1 0 9 9m-12-3 6 6m0-6-6 6",
  },
  hanging_piece: {
    label: "Hanging piece",
    definition:
      "A piece exposed to capture without adequate protection. A structural badge identifies exposure; a verified line establishes the consequence.",
    path: "m9 5 2-2a5 5 0 0 1 7 7l-2 2M8 12l-2 2a5 5 0 0 0 7 7l2-2",
  },
  fork: {
    label: "Fork",
    definition:
      "One piece attacks two or more targets at once. The split arrow shows the relationship; supporting evidence determines whether it wins anything.",
    path: "M12 21v-9L4 4m0 5V4h5M12 12l8-8m-5 0h5v5",
  },
  pin: {
    label: "Pin",
    definition:
      "An aligned piece shields a king or more valuable target behind it. Moving a piece pinned to its king is illegal if it exposes check; other pins can be broken.",
    path: "M3 15h18m-4-4 4 4-4 4M10 10v10M20 3v5m-2-3h4",
  },
  skewer: {
    label: "Skewer",
    definition:
      "An attack on a valuable front piece exposes a second target behind it when the front piece moves. Unlike a pin, the front target is driven away.",
    path: "M3 12h18m-4-4 4 4-4 4M8 5v14m5-10v10",
  },
  discovered_attack: {
    label: "Discovered attack",
    definition:
      "Moving one piece opens an attack by another piece behind it. Inspect both the moving piece and the newly opened line.",
    path: "M3 18h18m-4-4 4 4-4 4M10 12V3m-4 4 4-4 4 4",
  },
  back_rank_threat: {
    label: "Back-rank tactic",
    definition:
      "An attack along the king's home rank while its escape squares are restricted. A threat is not necessarily a forced checkmate.",
    path: "M3 5h18M4 15h16m-5-5 5 5-5 5",
  },
  deflection: {
    label: "Deflection",
    definition:
      "A move draws or forces a defender away from a duty, leaving another target vulnerable. The continuation must show why the defender cannot simply stay.",
    path: "M5 21v-9h15m-6-6 6 6-6 6",
  },
  sacrifice: {
    label: "Sacrifice",
    definition:
      "Material is offered for a concrete continuation or compensation. The icon alone does not mean the offer is sound or brilliant.",
    path: "m12 3 9 9-9 9-9-9Z",
  },
  zwischenzug: {
    label: "Zwischenzug · in-between move",
    definition:
      "An intervening move, often forcing, is played before an expected recapture or reply. The inserted step changes the move order.",
    path: "M3 17h6V7h6v10h6m-4-4 4 4-4 4",
  },
  mating_net: {
    label: "Mating net",
    definition:
      "Coordinated attacks restrict the king's escape squares and create mating threats. Only a verified mating line establishes forced mate.",
    path: "M4 4h16v16H4ZM12 8v8m-4-4h8",
  },
  overloaded_defender: {
    label: "Overloaded defender",
    definition:
      "One defender has multiple duties it cannot maintain after a concrete forcing sequence. Merely defending two pieces does not establish overload.",
    path: "M12 21V9M3 5l9 4 9-4M3 3v6m18-6v6M8 21h8",
  },
  blunder: {
    label: "Blunders",
    definition:
      "Moves classified as large losses of verified position value. This is a move grade, not a tactical motif or a rating prediction.",
    path: "M3 6a3 3 0 0 1 6 0c0 3-3 3-3 7m0 5v1M15 6a3 3 0 0 1 6 0c0 3-3 3-3 7m0 5v1",
  },
  mistake: {
    label: "Mistakes",
    definition:
      "Moves classified as consequential losses of verified position value. Their tactical or positional cause is explained separately by the evidence.",
    path: question,
  },
  imprecision: {
    label: "Inaccuracies",
    definition:
      "Moves classified as smaller losses of verified position value. This grade describes the decision, not the game result.",
    path: "M3 6a3 3 0 0 1 6 0c0 3-3 3-3 7m0 5v1M18 3v10m0 5v1",
  },
  time_pressure: {
    label: "Time pressure",
    definition:
      "A decision made with limited clock time, as recorded in the game. Clock context alone does not prove that time pressure caused an error.",
    path: "M12 3a9 9 0 1 0 9 9M12 7v5l4 2",
  },
  low_time: {
    label: "Low time",
    definition:
      "A moment flagged for little remaining clock time. Read the recorded clock and format alongside the move evidence; it is not a tactical motif.",
    path: "M5 3h14M5 21h14M7 3v4l10 10v4M17 3v4L7 17v4",
  },
  opening_phase: {
    label: "Opening phase",
    definition:
      "Evidence from the opening stage of a game. A phase groups when decisions occurred; it does not label every move as a book move.",
    path: "M9 8a3 3 0 1 1 6 0c0 2-2 3-2 3l4 9H7l4-9s-2-1-2-3Z",
  },
  endgame_phase: {
    label: "Endgame phase",
    definition:
      "Evidence from positions classified as endgames. Fewer pieces change the practical task, but the phase itself is neither a strength nor a weakness.",
    path: "M12 3v7M9 6h6M5 11h14l-3 9H8Z",
  },
} as const;

export type PatternGlyphKind = keyof typeof PATTERN_GLYPHS;
export const isPatternGlyphKind = (motif: string): motif is PatternGlyphKind =>
  Object.prototype.hasOwnProperty.call(PATTERN_GLYPHS, motif);

export const PatternGlyph = ({ motif }: { motif: string }) => (
  <svg
    viewBox="0 0 24 24"
    width="82%"
    height="82%"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="square"
    strokeLinejoin="miter"
    aria-hidden="true"
    focusable="false"
    data-pattern-glyph={isPatternGlyphKind(motif) ? motif : "unknown"}
  >
    <path d={isPatternGlyphKind(motif) ? PATTERN_GLYPHS[motif].path : "M5 5h14v14H5Z"} />
  </svg>
);
